import { useEffect, useMemo } from 'react';

import { cn } from '@client/lib';

import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { useAccountAccordion } from '@/features/guided-financial-planning/hooks/use-account-accordion';
import { AccountCard } from './account-card';

interface AccountAccordionListProps {
  className?: string;
  onAllCompleteChange?: (allComplete: boolean) => void;
  disabled?: boolean;
}

export function AccountAccordionList({
  className,
  onAllCompleteChange,
  disabled,
}: AccountAccordionListProps) {
  const { accountShells, allComplete, expandedAssetTypeId, handleToggle, handleSaveComplete } =
    useAccountAccordion();
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();

  // Build allocation lookup from snapshot for completed sessions
  const allocationsBySourceId = useMemo(() => {
    if (!isSessionComplete) return {};
    const allocations = session?.stepData?.planComparisonSnapshot?.fundingAllocations ?? [];
    return allocations.reduce(
      (acc, allocation) => {
        acc[allocation.selfFundingSourceId] = allocation.amount;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [isSessionComplete, session?.stepData?.planComparisonSnapshot?.fundingAllocations]);

  useEffect(() => {
    onAllCompleteChange?.(allComplete);
  }, [allComplete, onAllCompleteChange]);

  return (
    <div className={cn('space-y-4', className)}>
      {accountShells.map(shell => (
        <AccountCard
          key={shell.fundingSourceId ?? shell.assetTypeId}
          assetTypeId={shell.assetTypeId}
          label={shell.label}
          isExpanded={expandedAssetTypeId === shell.assetTypeId}
          onToggle={() => handleToggle(shell.assetTypeId)}
          fundingSourceId={shell.fundingSourceId}
          existingSource={shell.selfFundingSource}
          onSaveComplete={() => handleSaveComplete(shell.assetTypeId)}
          disabled={disabled}
          allocationAmount={
            shell.fundingSourceId ? allocationsBySourceId[shell.fundingSourceId] : undefined
          }
        />
      ))}
    </div>
  );
}
